import type { RouteRecordRaw } from 'vue-router'
import { Permission } from '@/utils/permissionBitmask'
import { behandelaarRedirects } from './behandelaar-redirects'
import { restoreAgendaViewState } from './agenda-view-state'

/**
 * Core routes for the behandelaar (practitioner) app.
 *
 * These are the actual route definitions with components and meta.
 * Backwards compatibility redirects are in behandelaar-redirects.ts.
 */
const coreRoutes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: () => import('@/views/ForgotPasswordView.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/register',
    name: 'register',
    component: () => import('@/views/RegisterView.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/invitations/:token',
    name: 'accept-invitation',
    component: () => import('@/views/AcceptInvitationView.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/impersonate',
    name: 'impersonate',
    component: () => import('@/views/ImpersonateView.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/dashboard',
    name: 'dashboard',
    component: () => import('@/views/DashboardView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/signaleringen',
    name: 'signals',
    component: () => import('@/views/SignalsView.vue'),
    meta: { requiresAuth: true, requiredPermission: Permission.SIGNALS_READ }
  },
  {
    path: '/profile',
    name: 'profile',
    component: () => import('@/views/ProfileView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/organization',
    name: 'organization',
    component: () => import('@/views/OrganizationView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/organization/declareren',
    name: 'organization-declareren',
    component: () => import('@/views/DeclarerenView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/organization/subscription',
    name: 'organization-subscription',
    component: () => import('@/views/SubscriptionView.vue'),
    meta: { requiresAuth: true, requiredPermission: Permission.SUBSCRIPTION_READ }
  },
  {
    path: '/organization/practice',
    name: 'organization-practice-details',
    component: () => import('@/views/PracticeDetailsView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/organization/users',
    name: 'organization-users',
    component: () => import('@/views/UserManagementView.vue'),
    meta: { requiresAuth: true, requiredPermission: Permission.USERS_READ }
  },
  {
    path: '/organization/users/:userId',
    name: 'organization-user-detail',
    component: () => import('@/views/OrganizationUserView.vue'),
    meta: { requiresAuth: true, requiredPermission: Permission.USERS_READ }
  },
  {
    path: '/organization/roles',
    name: 'organization-roles',
    component: () => import('@/views/RolesView.vue'),
    meta: { requiresAuth: true, requiredPermission: Permission.ROLES_READ }
  },
  {
    path: '/organization/security',
    name: 'organization-security',
    component: () => import('@/views/OrganizationSecurityView.vue'),
    meta: { requiresAuth: true, requiredPermission: Permission.SECURITY_VIEW }
  },
  {
    path: '/organization/documents',
    name: 'organization-documents',
    component: () => import('@/views/OrganizationDocumentsView.vue'),
    meta: {
      requiresAuth: true,
      requiredPermission: [Permission.DOCUMENTS_MANAGE_LABELS, Permission.ORGANIZATION_SETTINGS_UPDATE]
    }
  },
  {
    path: '/organization/teams',
    name: 'organization-teams',
    component: () => import('@/views/TeamsView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/organization/teams/:teamId',
    name: 'organization-team-detail',
    component: () => import('@/views/TeamDetailView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/organization/locations',
    name: 'organization-locations',
    component: () => import('@/views/LocationsView.vue'),
    meta: { requiresAuth: true, requiredPermission: Permission.LOCATIONS_READ }
  },
  {
    path: '/organization/locations/:locationId',
    name: 'organization-location-detail',
    component: () => import('@/views/LocationDetailView.vue'),
    meta: { requiresAuth: true, requiredPermission: Permission.LOCATIONS_READ }
  },
  {
    path: '/organization/appointment-types',
    name: 'organization-appointment-types',
    component: () => import('@/views/AppointmentTypesView.vue'),
    meta: { requiresAuth: true, requiredPermission: Permission.APPOINTMENT_TYPES_READ }
  },
  {
    path: '/organization/appointment-types/:appointmentTypeId',
    name: 'organization-appointment-type-detail',
    component: () => import('@/views/AppointmentTypeDetailView.vue'),
    meta: { requiresAuth: true, requiredPermission: Permission.APPOINTMENT_TYPES_READ }
  },
  {
    path: '/organization/client-states',
    name: 'organization-client-states',
    component: () => import('@/views/ClientStatesView.vue'),
    meta: { requiresAuth: true, requiredPermission: Permission.CLIENT_STATES_READ }
  },
  {
    path: '/organization/client-states/:clientStateId',
    name: 'organization-client-state-detail',
    component: () => import('@/views/ClientStateDetailView.vue'),
    meta: { requiresAuth: true, requiredPermission: Permission.CLIENT_STATES_READ }
  },
  {
    path: '/form-submissions',
    name: 'form-submissions',
    component: () => import('@/views/SubmissionsListView.vue'),
    meta: { requiresAuth: true, requiredPermission: Permission.FORMS_EDIT }
  },
  {
    path: '/form-submissions/:id',
    name: 'form-submission-detail',
    component: () => import('@/views/SubmissionDetailView.vue'),
    meta: { requiresAuth: true, requiredPermission: Permission.FORMS_EDIT }
  },
  {
    path: '/organization/forms',
    redirect: '/organization/forms/questions'
  },
  {
    path: '/organization/forms/questions',
    name: 'organization-forms-questions',
    component: () => import('@/views/QuestionsView.vue'),
    meta: { requiresAuth: true, requiredPermission: Permission.QUESTIONS_MANAGE }
  },
  {
    path: '/organization/forms/list',
    name: 'organization-forms-list',
    component: () => import('@/views/FormsListView.vue'),
    meta: { requiresAuth: true, requiredPermission: Permission.FORMS_MANAGE }
  },
  {
    path: '/organization/forms/list/:formId',
    name: 'organization-forms-detail',
    component: () => import('@/views/FormBuilderDetailView.vue'),
    meta: { requiresAuth: true, requiredPermission: Permission.FORMS_MANAGE }
  },
  {
    path: '/organization/forms/list/:formId/settings',
    name: 'organization-form-settings',
    component: () => import('@/views/FormSettingsView.vue'),
    meta: { requiresAuth: true, requiredPermission: Permission.FORMS_MANAGE }
  },
  {
    path: '/organization/letter-templates',
    name: 'organization-letter-templates',
    component: () => import('@/views/LetterTemplatesListView.vue'),
    meta: { requiresAuth: true, requiredPermission: Permission.LETTER_TEMPLATES_MANAGE }
  },
  {
    path: '/organization/letter-templates/new',
    name: 'organization-letter-template-create',
    component: () => import('@/views/LetterTemplateEditorView.vue'),
    meta: { requiresAuth: true, requiredPermission: Permission.LETTER_TEMPLATES_MANAGE }
  },
  {
    path: '/organization/letter-templates/:id',
    name: 'organization-letter-template-edit',
    component: () => import('@/views/LetterTemplateEditorView.vue'),
    meta: { requiresAuth: true, requiredPermission: Permission.LETTER_TEMPLATES_MANAGE }
  },
  {
    path: '/organization/signals',
    name: 'organization-signal-definitions',
    component: () => import('@/views/SignalDefinitionsView.vue'),
    meta: { requiresAuth: true, requiredPermission: Permission.SIGNAL_DEFINITIONS_MANAGE }
  },
  {
    path: '/organization/signals/authoring',
    name: 'organization-signal-authoring',
    component: () => import('@/views/SignalDraftAuthoringView.vue'),
    meta: { requiresAuth: true, requiredPermission: Permission.SIGNAL_DEFINITIONS_MANAGE }
  },
  {
    path: '/organization/integrations',
    name: 'organization-integrations',
    component: () => import('@/views/IntegrationsView.vue'),
    meta: { requiresAuth: true, requiredPermission: Permission.INTEGRATIONS_MANAGE }
  },
  {
    path: '/organization/agenda-import',
    name: 'organization-agenda-import',
    component: () => import('@/views/AgendaImportView.vue'),
    meta: { requiresAuth: true, requiredPermission: Permission.IMPORTS_AGENDA }
  },
  {
    // Uploading an EPD dump (#3883). Its own screen rather than a second entry
    // on the agenda import: the two take different files by different means —
    // a workbook parsed in the request against an archive that goes straight to
    // Cloud Storage — and they are separately permissioned.
    path: '/organization/epd-import',
    name: 'organization-epd-import',
    component: () => import('@/views/EpdImportView.vue'),
    meta: { requiresAuth: true, requiredPermission: Permission.IMPORTS_EPD }
  },
  {
    // Reached by run id: an import is previewed, approved or rejected here
    // whatever produced it (#3777). Any import permission may read a preview —
    // seeing what an import would do is not more privileged than running one —
    // and deciding it is gated separately, inside the view (#3776).
    path: '/organization/imports/:runId',
    name: 'organization-import-preview',
    component: () => import('@/views/ImportPreviewView.vue'),
    meta: {
      requiresAuth: true,
      requiredPermission: [
        Permission.IMPORTS_ZPM,
        Permission.IMPORTS_AGENDA,
        Permission.IMPORTS_EPD,
        Permission.IMPORTS_CONTRACT_TARIFFS
      ]
    }
  },
  {
    path: '/organization/audit-log',
    name: 'organization-audit-log',
    component: () => import('@/views/AuditLogView.vue'),
    meta: { requiresAuth: true, requiredPermission: Permission.AUDIT_READ }
  },
  {
    path: '/clients',
    name: 'clients',
    component: () => import('@/views/ClientsView.vue'),
    meta: { requiresAuth: true, requiredPermission: Permission.CLIENTS_READ }
  },
  {
    path: '/client-groups',
    name: 'client-groups',
    component: () => import('@/views/ClientGroupsView.vue'),
    meta: { requiresAuth: true, requiredPermission: Permission.CLIENT_GROUPS_READ }
  },
  {
    path: '/client-groups/:groupId',
    name: 'client-group-detail',
    component: () => import('@/views/ClientGroupDetailView.vue'),
    meta: { requiresAuth: true, requiredPermission: Permission.CLIENT_GROUPS_READ }
  },
  {
    path: '/clients/:clientId',
    name: 'client-detail',
    component: () => import('@/views/ClientDetailView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/clients/:clientId/care-trajectories',
    name: 'client-care-trajectories',
    component: () => import('@/views/ClientSubpageView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/clients/:clientId/care-trajectories/:careTrajectoryId',
    name: 'care-trajectory-detail',
    component: () => import('@/views/ZorgtrajectDetailView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/clients/:clientId/billing',
    name: 'client-billing',
    component: () => import('@/views/ClientSubpageView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/clients/:clientId/dossier',
    name: 'client-dossier',
    redirect: to => ({ name: 'client-dossier-overview', params: { clientId: to.params.clientId } }),
    meta: { requiresAuth: true }
  },
  {
    path: '/clients/:clientId/dossier/overview',
    name: 'client-dossier-overview',
    component: () => import('@/views/ClientSubpageView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/clients/:clientId/dossier/documents',
    name: 'client-dossier-documents',
    component: () => import('@/views/ClientSubpageView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/clients/:clientId/dossier/appointments',
    name: 'client-dossier-appointments',
    component: () => import('@/views/ClientSubpageView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/clients/:clientId/dossier/answers',
    name: 'client-dossier-answers',
    component: () => import('@/views/ClientSubpageView.vue'),
    meta: { requiresAuth: true, requiredPermission: Permission.FORMS_EDIT }
  },
  {
    path: '/clients/:clientId/dossier/zorgkring',
    name: 'client-dossier-zorgkring',
    component: () => import('@/views/ClientSubpageView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/clients/:clientId/dossier/appointments/:appointmentId',
    name: 'client-dossier-appointment-detail',
    component: () => import('@/views/ClientSubpageView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/agenda',
    name: 'agenda',
    component: () => import('@/views/AgendaView.vue'),
    // Rehydrate the view-state the user left the agenda with when they come
    // back through a link that carries no query of its own (#3244). Only fires
    // on entering the route, so query updates made by the agenda itself pass
    // through untouched.
    beforeEnter: (to) => restoreAgendaViewState(to) ?? true,
    meta: { requiresAuth: true, requiredPermission: Permission.APPOINTMENTS_READ }
  },
  {
    // Legacy route kept so old bookmarks and existing `agenda-appointment`
    // call sites land on the standalone appointment detail page instead of
    // opening a modal over the agenda.
    path: '/agenda/:appointmentId',
    name: 'agenda-appointment',
    redirect: (to) => ({
      name: 'appointment-detail',
      params: { id: to.params.appointmentId }
    }),
    meta: { requiresAuth: true }
  },
  {
    // The retired EventCalendar agenda; a bookmark lands on the agenda.
    path: '/agenda2',
    redirect: { name: 'agenda' }
  },
  {
    path: '/appointments/:id',
    name: 'appointment-detail',
    component: () => import('@/views/AppointmentDetailView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/invoices',
    name: 'invoices',
    component: () => import('@/views/FacturenView.vue'),
    meta: { requiresAuth: true, requiredPermission: Permission.BILLING_READ }
  },
  {
    path: '/invoices/:invoiceId',
    name: 'invoice-detail',
    component: () => import('@/views/InvoiceDetailView.vue'),
    meta: { requiresAuth: true, requiredPermission: Permission.BILLING_READ }
  },
  {
    path: '/contracts',
    name: 'contracts',
    component: () => import('@/views/ContractsView.vue'),
    meta: { requiresAuth: true }
  },
  {
    // A gemeente contract (#4793) is not an insurer contract — `/contracts/:contractId` answers
    // 404 for its id — so it has a detail screen of its own, reached from its row in the list.
    path: '/contracts/gemeente/:contractId',
    name: 'municipal-contract-detail',
    component: () => import('@/views/MunicipalContractDetailView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/contracts/new',
    name: 'contract-create',
    component: () => import('@/views/ContractDetailView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/contracts/:contractId',
    name: 'contract-detail',
    component: () => import('@/views/ContractDetailView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/contracts/:contractId/edit',
    name: 'contract-edit',
    component: () => import('@/views/ContractDetailView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/claims',
    component: () => import('@/views/DeclaratiesView.vue'),
    meta: { requiresAuth: true, requiredPermission: Permission.BILLING_READ },
    children: [
      {
        path: '',
        redirect: '/claims/insurer'
      },
      {
        path: 'insurer',
        name: 'claims-insurer',
        component: () => import('@/views/ZorgverzekeraarView.vue')
      },
      {
        path: 'batches',
        name: 'claims-batches',
        component: () => import('@/views/BatchesView.vue')
      },
      {
        path: 'private',
        name: 'claims-private',
        component: () => import('@/views/ParticulierView.vue')
      },
      {
        path: 'invoices',
        name: 'claims-invoices',
        component: () => import('@/views/FacturenView.vue'),
        props: { nested: true }
      },
      {
        // The gemeente declaraties (#4664): the guard turns the route away for an
        // organisation without Jeugdwet enabled, as DeclaratiesView hides its tab.
        path: 'municipality',
        name: 'claims-municipality',
        component: () => import('@/components/organisms/jeugdwet/GemeenteDeclaraties.vue'),
        meta: { requiresJeugdwet: true }
      }
    ]
  },
  {
    path: '/mfa/setup',
    name: 'mfa-setup',
    component: () => import('@/views/MfaSetupView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/mfa/challenge',
    name: 'mfa-challenge',
    component: () => import('@/views/MfaChallengeView.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/analytics',
    name: 'analytics',
    component: () => import('@/views/BillingInsightView.vue'),
    meta: { requiresAuth: true, requiredPermission: Permission.BILLING_READ }
  },
  {
    // The same authority as the chart it sits beside: the endpoint behind it guards on
    // `billing.read` too, and every declaratieregel already names its behandelaar (#4561).
    path: '/analytics/declarabiliteit',
    name: 'analytics-declarabiliteit',
    component: () => import('@/views/DeclarabiliteitView.vue'),
    meta: { requiresAuth: true, requiredPermission: Permission.BILLING_READ }
  },
  {
    // The iJw berichten (#4665): a place of its own in the app and the side menu, for an
    // organisation with Jeugdwet enabled.
    path: '/berichten',
    name: 'berichten',
    component: () => import('@/views/BerichtenView.vue'),
    meta: { requiresAuth: true, requiresJeugdwet: true }
  },
  {
    path: '/',
    redirect: '/dashboard'
  }
]

/**
 * All behandelaar routes: core routes + backwards compatibility redirects.
 */
export const behandelaarRoutes: RouteRecordRaw[] = [
  ...coreRoutes,
  ...behandelaarRedirects
]
